import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  CAlert,
  CButton,
  CContainer,
  CLoadingButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from "@coreui/react-pro";
import { useNavigate, useSearchParams } from "react-router-dom";
import PublicSiteShell from "../../components/public/PublicSiteShell";
import stb_config from "../../config/config";
import { buildAuthRequestConfig, getStoredIdentity } from "../../utils/authSession";
import { isPrerenderUserAgent } from "../../utils/prerender";
import { normalizeRole } from "../../utils/roleMap";
import "../../styles/public-experience.css";

const REGION_OPTIONS = [
  { value: "all", label: "Alle Regionen" },
  { value: "DE", label: "Deutschland" },
  { value: "AT", label: "Österreich" },
  { value: "CH", label: "Schweiz" },
];

const HERO_SIGNALS = [
  "Keine klassischen Vermittlungsstrukturen",
  "Klare Anforderungen und Rahmenbedingungen",
  "Fokus auf Vorbereitung, Transparenz und Ankommen",
];

const HERO_CALLOUTS = [
  {
    title: "Direkter Zugang",
    text: "Offene Stellen werden ohne dunkle Zwischenwelt direkt in die öffentliche Experts4Europe-Erfahrung eingebunden.",
  },
  {
    title: "Klar einordnen",
    text: "Titel, Region, Arbeitsform, Bonus und Ankunftshilfe bleiben nachvollziehbar und gut lesbar strukturiert.",
  },
  {
    title: "Serioser Marktauftritt",
    text: "Die Jobsuche ist Teil des gesamten Experts4Europe-Weges und bleibt auf Transparenz und Vorbereitung ausgerichtet.",
  },
];

const LOADING_PLACEHOLDERS = Array.from({ length: 6 }, (_, index) => index);

function getHousingSupportLabel(posting) {
  return posting.wohnungsunterstuetzung_art === "unterkunft_wird_gestellt"
    ? "Unterkunft wird gestellt"
    : "Hilfe bei der Unterkunftssuche";
}

function getLocationLabel(posting, fallback = "Global") {
  return [posting.standort_land, posting.standort_ort].filter(Boolean).join(" / ") || fallback;
}

function getBonusLabel(posting) {
  if (posting.onboarding_bonus === null || posting.onboarding_bonus === undefined || posting.onboarding_bonus === "") {
    return "Bonus offen";
  }

  return `${posting.onboarding_bonus} EUR`;
}

export default function StbJobMarketplace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stb_isPrerender = isPrerenderUserAgent();
  const [stb_jobPostings, setStb_jobPostings] = useState([]);
  const [stb_searchTerm, setStb_searchTerm] = useState("");
  const [stb_selectedRegion, setStb_selectedRegion] = useState("all");
  const [stb_loading, setStb_loading] = useState(true);
  const [stb_error, setStb_error] = useState("");
  const [selectedPosting, setSelectedPosting] = useState(null);
  const [applyDialogPosting, setApplyDialogPosting] = useState(null);
  const [applyIntent, setApplyIntent] = useState(null);
  const [applyIntentLoading, setApplyIntentLoading] = useState(false);
  const [applyActionLoading, setApplyActionLoading] = useState(false);
  const [applyStatusMessage, setApplyStatusMessage] = useState(null);
  const [marketplaceFlash, setMarketplaceFlash] = useState(null);
  const resumeHandledRef = useRef("");

  const storedIdentity = getStoredIdentity();
  const authRole = normalizeRole(storedIdentity.role);
  const isApplicantSession = Boolean(storedIdentity.userid) && authRole === "applicant";

  const buildApplyLoginPath = (jobId) => {
    const params = new URLSearchParams();
    params.set("returnTo", "/stb/job-marketplace");
    params.set("intent", "apply");
    params.set("jobId", String(jobId));
    params.set("role", "applicant");
    return `/login?${params.toString()}`;
  };

  const loadApplyIntent = async (jobId) => {
    setApplyIntentLoading(true);
    setApplyStatusMessage(null);
    try {
      const response = await axios.get(`${stb_config.API_BASE_URL}/api/stb/ads/${jobId}/apply-intent`, {
        ...buildAuthRequestConfig(),
      });
      setApplyIntent(response.data || null);
    } catch (error) {
      if (Number(error?.response?.status || 0) === 401) {
        navigate(buildApplyLoginPath(jobId));
        return;
      }
      setApplyIntent(null);
      setApplyStatusMessage({
        color: "danger",
        text: error?.response?.data?.message || "Der Bewerbungsstatus konnte gerade nicht geladen werden.",
      });
    } finally {
      setApplyIntentLoading(false);
    }
  };

  const openApplyDialog = async (posting, options = {}) => {
    setApplyDialogPosting(posting);
    setApplyIntent(null);
    setApplyStatusMessage(null);
    if (options.resume) {
      setMarketplaceFlash({
        color: "info",
        text: "Dein Bewerbungswunsch wurde wiederhergestellt. Prüfe jetzt noch einmal Merken oder Bewerben.",
      });
    }
    await loadApplyIntent(posting.id);
  };

  const closeApplyDialog = () => {
    setApplyDialogPosting(null);
    setApplyIntent(null);
    setApplyStatusMessage(null);
    setApplyIntentLoading(false);
    setApplyActionLoading(false);
  };

  const handleApplyEntry = async (posting) => {
    if (!isApplicantSession) {
      navigate(buildApplyLoginPath(posting.id));
      return;
    }
    await openApplyDialog(posting);
  };

  const handleSaveJob = async () => {
    if (!applyDialogPosting) return;
    setApplyActionLoading(true);
    setApplyStatusMessage(null);
    try {
      const response = await axios.post(
        `${stb_config.API_BASE_URL}/api/stb/ads/${applyDialogPosting.id}/save`,
        {},
        buildAuthRequestConfig()
      );
      setApplyIntent((current) => current ? { ...current, saved: true } : current);
      const text = response.data?.message || "Die Stelle wurde gemerkt.";
      setApplyStatusMessage({ color: "success", text });
      setMarketplaceFlash({ color: "success", text });
    } catch (error) {
      if (Number(error?.response?.status || 0) === 401) {
        navigate(buildApplyLoginPath(applyDialogPosting.id));
        return;
      }
      setApplyStatusMessage({
        color: "danger",
        text: error?.response?.data?.message || "Merken konnte gerade nicht abgeschlossen werden.",
      });
    } finally {
      setApplyActionLoading(false);
    }
  };

  const handleSubmitApplication = async () => {
    if (!applyDialogPosting) return;
    setApplyActionLoading(true);
    setApplyStatusMessage(null);
    try {
      const response = await axios.post(
        `${stb_config.API_BASE_URL}/api/stb/ads/${applyDialogPosting.id}/apply`,
        {},
        buildAuthRequestConfig()
      );
      const payload = response.data || {};
      if (payload.blocked) {
        setApplyIntent((current) => current ? { ...current, eligibility: payload.eligibility || current.eligibility } : current);
        setApplyStatusMessage({ color: "warning", text: payload.message || "Vor der Bewerbung fehlen noch zwingende Grundlagen." });
        return;
      }
      if (payload.duplicate) {
        setApplyIntent((current) => current ? {
          ...current,
          duplicate: true,
          existing_application: {
            id: payload.application_id,
            status: payload.application_status,
            candidate_ref: payload.candidate_ref || current?.existing_application?.candidate_ref || "",
          },
        } : current);
        setApplyStatusMessage({ color: "warning", text: payload.message || "Für diese Stelle existiert bereits eine Bewerbung." });
        setMarketplaceFlash({ color: "warning", text: payload.message || "Für diese Stelle existiert bereits eine Bewerbung." });
        return;
      }

      const successText = payload.message || "Die Bewerbung wurde erfolgreich angelegt.";
      setMarketplaceFlash({ color: "success", text: successText });
      closeApplyDialog();
    } catch (error) {
      if (Number(error?.response?.status || 0) === 401) {
        navigate(buildApplyLoginPath(applyDialogPosting.id));
        return;
      }
      setApplyStatusMessage({
        color: "danger",
        text: error?.response?.data?.message || "Die Bewerbung konnte gerade nicht angelegt werden.",
      });
    } finally {
      setApplyActionLoading(false);
    }
  };

  const stb_fetchJobPostings = async () => {
    if (stb_isPrerender) {
      setStb_loading(false);
      setStb_error("");
      setStb_jobPostings([]);
      return;
    }

    setStb_loading(true);

    try {
      const stb_response = await axios.get(`${stb_config.API_BASE_URL}/api/stb/ads/public`, {
        params: { page: 1, limit: 60 },
      });

      setStb_jobPostings(Array.isArray(stb_response.data?.rows) ? stb_response.data.rows : []);
      setStb_error("");
    } catch (stb_err) {
      console.error("stb_Error fetching job postings:", stb_err);
      setStb_error("Fehler beim Laden der Suchanzeigen");
    } finally {
      setStb_loading(false);
    }
  };

  useEffect(() => {
    document.title = "Stellenbörse | Experts4Europe";
    if (stb_isPrerender) {
      setStb_loading(false);
      return;
    }
    stb_fetchJobPostings();
  }, [stb_isPrerender]);

  useEffect(() => {
    if (!selectedPosting) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedPosting(null);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedPosting]);

  useEffect(() => {
    const requestedJobId = String(searchParams.get("jobId") || "").trim();
    if (!requestedJobId || !stb_jobPostings.length) return;
    const match = stb_jobPostings.find((entry) => String(entry.id) === requestedJobId);
    if (!match) return;
    if (!selectedPosting || String(selectedPosting.id) !== requestedJobId) {
      setSelectedPosting(match);
    }
  }, [searchParams, stb_jobPostings, selectedPosting]);

  useEffect(() => {
    const requestedIntent = String(searchParams.get("intent") || "").trim();
    const requestedJobId = String(searchParams.get("jobId") || "").trim();
    if (requestedIntent !== "apply" || !requestedJobId || !stb_jobPostings.length || !isApplicantSession) return;

    const match = stb_jobPostings.find((entry) => String(entry.id) === requestedJobId);
    if (!match) return;

    const resumeKey = `${requestedJobId}:${getStoredIdentity().userid || ""}`;
    if (resumeHandledRef.current === resumeKey) return;
    resumeHandledRef.current = resumeKey;

    setSelectedPosting(match);
    openApplyDialog(match, { resume: true });

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("intent");
    nextParams.delete("jobId");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, stb_jobPostings, isApplicantSession]);

  const stb_filteredPostings = useMemo(() => {
    const search = stb_searchTerm.trim().toLowerCase();

    return stb_jobPostings.filter((stb_posting) => {
      const titel = String(stb_posting.titel || "").toLowerCase();
      const beschreibung = String(stb_posting.beschreibung || "").toLowerCase();
      const beruf = String(stb_posting.beruf_text || "").toLowerCase();
      const ort = String(stb_posting.standort_ort || "").toLowerCase();
      const land = String(stb_posting.standort_land || "").toUpperCase();

      const stb_matchesSearch =
        !search ||
        titel.includes(search) ||
        beschreibung.includes(search) ||
        beruf.includes(search) ||
        ort.includes(search);
      const stb_matchesRegion = stb_selectedRegion === "all" || land === stb_selectedRegion;

      return stb_matchesSearch && stb_matchesRegion;
    });
  }, [stb_jobPostings, stb_searchTerm, stb_selectedRegion]);

  const stb_activeFilters = useMemo(() => {
    const filters = [];

    if (stb_searchTerm.trim()) {
      filters.push(`Suche: "${stb_searchTerm.trim()}"`);
    }

    if (stb_selectedRegion !== "all") {
      const regionLabel = REGION_OPTIONS.find((option) => option.value === stb_selectedRegion)?.label || stb_selectedRegion;
      filters.push(`Region: ${regionLabel}`);
    }

    return filters;
  }, [stb_searchTerm, stb_selectedRegion]);

  let stb_resultHeading = "Stellen werden geladen";
  let stb_resultCopy = "Die öffentlichen Anzeigen werden gerade aus dem Experts4Europe-System geladen.";
  const selectedPostingApplyHint = !isApplicantSession
    ? "Beim Klick auf Bewerben geht es geordnet über den Login weiter und der Stellenkontext bleibt erhalten."
    : authRole !== "applicant"
      ? "Aktuell ist kein Bewerberkonto aktiv. Der Bewerbungsweg wechselt deshalb geordnet in den Login."
      : "Vor dem finalen Submit prüft Experts4Europe echte Duplikate und die wichtigsten Applicant-Grundlagen.";

  if (stb_isPrerender) {
    stb_resultHeading = "Stellenvorschau";
    stb_resultCopy =
      "Die Live-Stellen werden für den statischen Preview-Lauf nicht vorab geladen und erscheinen nach dem Öffnen im Browser.";
  } else if (stb_error) {
    stb_resultHeading = "Öffentliche Stellen aktuell nicht erreichbar";
    stb_resultCopy =
      "Die Public-Seite bleibt der richtige Einstieg für Jobs, Regionen und Rahmenbedingungen. Der Datenabruf kann erneut gestartet werden.";
  } else if (!stb_loading && stb_filteredPostings.length === 0) {
    stb_resultHeading = "Keine Treffer für die aktuelle Auswahl";
    stb_resultCopy =
      "Passe Suchbegriff oder Region an, damit wieder passende Stellen innerhalb derselben Filterlogik angezeigt werden.";
  } else if (!stb_loading) {
    stb_resultHeading = `${stb_filteredPostings.length} ${stb_filteredPostings.length === 1 ? "Stelle" : "Stellen"} im Überblick`;
    stb_resultCopy =
      "Die Ergebnisse folgen derselben Such- und Filterlogik wie bisher. Öffne eine Karte für Details zu Anforderungen, Ankunftshilfe und Bonus.";
  }

  const hero = (
    <section className="public-listing-hero">
      <CContainer fluid className="public-core-container public-listing-shell public-listing-shell--hero public-page-shell">
        <div className="public-listing-hero__panel public-page-hero-panel">
          <div className="public-listing-hero__copy">
            <span className="public-core-eyebrow public-page-overline">Stellenbörse</span>
            <h1 className="public-page-title public-listing-hero__title">Stellenbörse</h1>
            <p className="public-listing-hero__lead public-page-body">
              Direkter Zugang zu europäischen Arbeitgebern mit klaren Anforderungen, transparenter Einordnung und nachvollziehbaren Rahmenbedingungen.
            </p>

            <div className="public-listing-hero__signals" aria-label="Orientierung zur Stellenbörse">
              {HERO_SIGNALS.map((item) => (
                <span key={item} className="public-listing-signal">
                  {item}
                </span>
              ))}
            </div>
          </div>

          <aside className="public-listing-hero__meta" aria-label="Seriöse Einordnung">
            {HERO_CALLOUTS.map((item) => (
              <article key={item.title} className="public-listing-callout public-page-callout">
                <strong>{item.title}</strong>
                <p>{item.text}</p>
              </article>
            ))}
          </aside>
        </div>
      </CContainer>
    </section>
  );

  return (
    <PublicSiteShell pageClassName="public-listing-page public-job-marketplace-page" hero={hero}>
      <section className="public-core-band public-core-band--light">
        <CContainer fluid className="public-core-container public-listing-shell public-listing-shell--content public-page-shell">
          <div className="public-listing-stack">
            <section className="public-listing-filterbar public-page-card" aria-labelledby="stb-filters-heading">
              <div className="public-listing-filterbar__head">
                <div className="public-listing-filterbar__copy">
                  <span className="public-core-eyebrow public-page-overline">Jobsuche</span>
                  <h2 id="stb-filters-heading" className="public-page-section-title">
                    Suche und Filter
                  </h2>
                  <p className="public-page-body public-page-body--muted">
                    Suche nach Titel, Stichwort, Beruf oder Ort und filtere wie bisher nach Region.
                  </p>
                </div>
              </div>

              <div className="public-listing-filterbar__workspace">
                <div className="public-listing-filterbar__form">
                  <label className="public-listing-field">
                    <span>Suche</span>
                    <input
                      type="text"
                      placeholder="Jobtitel, Stichworte oder Ort"
                      value={stb_searchTerm}
                      onChange={(event) => setStb_searchTerm(event.target.value)}
                      className="public-listing-input"
                    />
                  </label>

                  <label className="public-listing-field public-listing-field--compact">
                    <span>Region</span>
                    <select
                      value={stb_selectedRegion}
                      onChange={(event) => setStb_selectedRegion(event.target.value)}
                      className="public-listing-select"
                    >
                      {REGION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="public-listing-filterbar__status" aria-live="polite">
                  <strong>{stb_jobPostings.length}</strong>
                  <span>aktuelle Anzeigen im öffentlichen Pool</span>
                </div>
              </div>
            </section>

            <section className="public-listing-results" aria-labelledby="stb-results-heading">
              <div className="public-listing-results__info public-page-card">
                <div className="public-listing-results__copy">
                  <span className="public-core-eyebrow public-page-overline">Ergebnisse</span>
                  <h2 id="stb-results-heading" className="public-page-subtitle">
                    {stb_resultHeading}
                  </h2>
                  <p className="public-page-body public-page-body--muted">{stb_resultCopy}</p>
                </div>

                <div className="public-listing-results__state">
                  <span className="public-listing-results__state-label">Aktive Auswahl</span>
                  <div className="public-listing-results__chips" aria-label="Aktive Filter">
                    {stb_activeFilters.length > 0 ? (
                      stb_activeFilters.map((filter) => (
                        <span key={filter} className="public-listing-chip">
                          {filter}
                        </span>
                      ))
                    ) : (
                      <span className="public-listing-chip public-listing-chip--muted">Keine aktiven Zusatzfilter</span>
                    )}
                  </div>
                </div>
              </div>

              {stb_loading ? (
                <div className="public-listing-grid" aria-live="polite">
                  {LOADING_PLACEHOLDERS.map((placeholder) => (
                    <article
                      key={placeholder}
                      className="public-listing-card public-listing-card--loading public-page-card"
                      aria-hidden="true"
                    >
                      <span className="public-listing-skeleton public-listing-skeleton--label" />
                      <span className="public-listing-skeleton public-listing-skeleton--title" />
                      <span className="public-listing-skeleton public-listing-skeleton--line" />
                      <span className="public-listing-skeleton public-listing-skeleton--line public-listing-skeleton--line-short" />
                      <div className="public-listing-skeleton-grid">
                        <span className="public-listing-skeleton public-listing-skeleton--fact" />
                        <span className="public-listing-skeleton public-listing-skeleton--fact" />
                        <span className="public-listing-skeleton public-listing-skeleton--fact" />
                        <span className="public-listing-skeleton public-listing-skeleton--fact" />
                      </div>
                      <span className="public-listing-skeleton public-listing-skeleton--button" />
                    </article>
                  ))}
                </div>
              ) : stb_isPrerender ? (
                <article className="public-listing-empty public-page-card">
                  <div className="public-listing-empty__copy">
                    <span className="public-core-eyebrow public-page-overline">Statische Vorschau</span>
                    <h3 className="public-page-subtitle">Die Live-Stellenbörse wird im Browser nachgeladen</h3>
                    <p className="public-page-body public-page-body--muted">
                      Für den Prerender-Lauf bleibt diese Surface absichtlich ohne Live-API-Abruf. Nach dem Öffnen im Browser werden die öffentlichen Stellen wieder normal geladen.
                    </p>
                  </div>

                  <div className="public-listing-empty__highlights public-listing-empty__highlights--compact">
                    <article className="public-listing-empty__highlight">
                      <strong>Warum das so ist</strong>
                      <p>Der statische Build soll keine Live-API-Fehler oder unvollständigen Antworten in die Vorschau ziehen.</p>
                    </article>
                    <article className="public-listing-empty__highlight">
                      <strong>Was im Browser passiert</strong>
                      <p>Die bestehende Such-, Filter- und Kartenlogik wird nach dem Laden der Seite unverändert aktiviert.</p>
                    </article>
                  </div>
                </article>
              ) : stb_error ? (
                <article className="public-listing-empty public-listing-empty--error public-page-card">
                  <div className="public-listing-empty__copy">
                    <span className="public-core-eyebrow public-page-overline">Verbindung prüfen</span>
                    <h3 className="public-page-subtitle">Aktuelle Stellen konnten gerade nicht geladen werden</h3>
                    <p className="public-page-body public-page-body--muted">
                      Die Stellenbörse bleibt dein öffentlicher Einstieg für Jobs, Regionen und nachvollziehbare Rahmenbedingungen. Der Abruf kann direkt erneut gestartet werden.
                    </p>
                  </div>

                  <div className="public-listing-empty__actions">
                    <CButton type="button" className="public-core-btn public-core-btn--primary" onClick={stb_fetchJobPostings}>
                      Erneut laden
                    </CButton>
                  </div>

                  <div className="public-listing-empty__highlights">
                    <article className="public-listing-empty__highlight">
                      <strong>Was du findest</strong>
                      <p>Offene Jobs für internationale Fachkräfte in Europa.</p>
                    </article>
                    <article className="public-listing-empty__highlight">
                      <strong>Was du filtern kannst</strong>
                      <p>Suche nach Titel, Beruf, Ort und Region mit derselben bestehenden Logik.</p>
                    </article>
                    <article className="public-listing-empty__highlight">
                      <strong>Was als Nächstes folgt</strong>
                      <p>Nach dem erneuten Abruf werden aktuelle Anzeigen automatisch wieder in derselben Seite dargestellt.</p>
                    </article>
                  </div>
                </article>
              ) : stb_filteredPostings.length === 0 ? (
                <article className="public-listing-empty public-page-card">
                  <div className="public-listing-empty__copy">
                    <span className="public-core-eyebrow public-page-overline">Keine Treffer</span>
                    <h3 className="public-page-subtitle">Zur aktuellen Auswahl gibt es keine passenden Stellen</h3>
                    <p className="public-page-body public-page-body--muted">
                      Die Such- und Filterlogik arbeitet weiter wie bisher. Passe Suchbegriff oder Region an, um weitere Ergebnisse sichtbar zu machen.
                    </p>
                  </div>

                  <div className="public-listing-empty__highlights public-listing-empty__highlights--compact">
                    <article className="public-listing-empty__highlight">
                      <strong>Suche prüfen</strong>
                      <p>Probiere einen allgemeineren Titel, ein Stichwort oder nur den Ort.</p>
                    </article>
                    <article className="public-listing-empty__highlight">
                      <strong>Region erweitern</strong>
                      <p>Stelle den Filter auf "Alle Regionen", um den gesamten öffentlichen Pool zu sehen.</p>
                    </article>
                  </div>
                </article>
              ) : (
                <div className="public-listing-grid">
                  {stb_filteredPostings.map((stb_posting) => {
                    const primaryFacts = [
                      { label: "Region", value: getLocationLabel(stb_posting) },
                      { label: "Arbeitsform", value: stb_posting.arbeitsform || "Arbeitsform offen" },
                    ];
                    const secondaryFacts = [
                      { label: "Ankunftshilfe", value: getHousingSupportLabel(stb_posting) },
                      { label: "Bonus", value: getBonusLabel(stb_posting) },
                    ];

                    return (
                      <article key={stb_posting.id} className="public-listing-card public-page-card">
                        <div className="public-listing-card__head">
                          <p className="public-listing-card__eyebrow">{stb_posting.beruf_text || "Öffentliche Stelle"}</p>
                          <h3 className="public-listing-card__title">{stb_posting.titel || "Unbenannte Stelle"}</h3>
                        </div>

                        <dl className="public-listing-card__primary">
                          {primaryFacts.map((fact) => (
                            <div key={fact.label}>
                              <dt>{fact.label}</dt>
                              <dd>{fact.value}</dd>
                            </div>
                          ))}
                        </dl>

                        <p className="public-listing-card__snippet">
                          {stb_posting.beschreibung || "Für diese Stelle liegt aktuell keine Kurzbeschreibung vor."}
                        </p>

                        <dl className="public-listing-card__secondary">
                          {secondaryFacts.map((fact) => (
                            <div key={fact.label}>
                              <dt>{fact.label}</dt>
                              <dd>{fact.value}</dd>
                            </div>
                          ))}
                        </dl>

                        {stb_posting.skills ? (
                          <p className="public-listing-card__skills">
                            <strong>Relevante Zusatzinfos</strong>
                            <span>{stb_posting.skills}</span>
                          </p>
                        ) : null}

                        <div className="public-listing-card__footer">
                          <CButton
                            type="button"
                            className="public-core-btn public-core-btn--primary public-core-btn--block"
                            onClick={() => setSelectedPosting(stb_posting)}
                          >
                            Mehr erfahren
                          </CButton>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </CContainer>
      </section>

      {selectedPosting ? (
        <div className="public-listing-modal" role="dialog" aria-modal="true" aria-labelledby="stb-modal-title">
          <button
            type="button"
            className="public-listing-modal__backdrop"
            onClick={() => setSelectedPosting(null)}
            aria-label="Detailansicht schließen"
          />

          <div className="public-listing-modal__surface public-page-card">
            <div className="public-listing-modal__header">
              <div className="public-listing-modal__intro">
                <p className="public-listing-card__eyebrow">{selectedPosting.beruf_text || "Öffentliche Stelle"}</p>
                <h2 id="stb-modal-title" className="public-page-section-title public-listing-modal__title">
                  {selectedPosting.titel || "Unbenannte Stelle"}
                </h2>
                <p className="public-page-body public-page-body--muted">
                  {getLocationLabel(selectedPosting, "Standort offen")} · {selectedPosting.arbeitsform || "Arbeitsform offen"}
                </p>
              </div>

              <CButton
                type="button"
                className="public-core-btn public-core-btn--light"
                onClick={() => setSelectedPosting(null)}
              >
                Schließen
              </CButton>
            </div>

            <div className="public-listing-modal__content">
              <div className="public-listing-modal__sections">
                <section className="public-listing-detail">
                  <h3>Beschreibung</h3>
                  <p>{selectedPosting.beschreibung || "-"}</p>
                </section>

                <section className="public-listing-detail">
                  <h3>Aufgaben</h3>
                  <p>{selectedPosting.aufgaben || "-"}</p>
                </section>

                <section className="public-listing-detail">
                  <h3>Anforderungen</h3>
                  <p>{selectedPosting.anforderungen || "-"}</p>
                </section>
              </div>

              <aside className="public-listing-modal__facts">
                <article className="public-listing-fact public-listing-fact--wide public-listing-apply-panel">
                  <span>Bewerbung</span>
                  <strong>Jetzt geordnet in den Bewerbungsfluss einsteigen</strong>
                  <p>{selectedPostingApplyHint}</p>
                  <div className="public-listing-apply-panel__actions">
                    <CButton
                      type="button"
                      className="public-core-btn public-core-btn--primary public-listing-apply-panel__primary"
                      onClick={() => handleApplyEntry(selectedPosting)}
                    >
                      Bewerben
                    </CButton>
                  </div>
                  {marketplaceFlash ? (
                    <CAlert color={marketplaceFlash.color} className="public-listing-inline-alert">
                      {marketplaceFlash.text}
                    </CAlert>
                  ) : null}
                </article>
                <article className="public-listing-fact">
                  <span>Region</span>
                  <strong>{getLocationLabel(selectedPosting, "Standort offen")}</strong>
                </article>
                <article className="public-listing-fact">
                  <span>Arbeitsform</span>
                  <strong>{selectedPosting.arbeitsform || "Arbeitsform offen"}</strong>
                </article>
                <article className="public-listing-fact">
                  <span>Willkommensbonus</span>
                  <strong>{getBonusLabel(selectedPosting)}</strong>
                </article>
                <article className="public-listing-fact">
                  <span>Wohnunterstützung</span>
                  <strong>{getHousingSupportLabel(selectedPosting)}</strong>
                </article>
                {selectedPosting.skills ? (
                  <article className="public-listing-fact public-listing-fact--wide">
                    <span>Relevante Zusatzinfos</span>
                    <strong>{selectedPosting.skills}</strong>
                  </article>
                ) : null}
              </aside>
            </div>
          </div>
        </div>
      ) : null}

      <CModal
        alignment="center"
        visible={Boolean(applyDialogPosting)}
        onClose={closeApplyDialog}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>Bist du sicher, dass du dich auf diesen Arbeitsplatz bewerben möchtest?</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div className="public-apply-confirm__stack">
            <div className="public-apply-confirm__summary">
              <strong>{applyDialogPosting?.titel || "Unbenannte Stelle"}</strong>
              <span>{applyDialogPosting ? `${getLocationLabel(applyDialogPosting, "Standort offen")} · ${applyDialogPosting.arbeitsform || "Arbeitsform offen"}` : ""}</span>
            </div>

            {applyIntentLoading ? (
              <CAlert color="info" className="mb-0">
                Bewerbungsstatus und Mindestvoraussetzungen werden geprüft...
              </CAlert>
            ) : null}

            {applyStatusMessage ? (
              <CAlert color={applyStatusMessage.color} className="mb-0">
                {applyStatusMessage.text}
              </CAlert>
            ) : null}

            {applyIntent?.duplicate ? (
              <CAlert color="warning" className="mb-0">
                Für diese Stelle gibt es bereits eine Bewerbung. Doppeltes Anlegen wird deshalb sauber verhindert.
              </CAlert>
            ) : null}

            {!applyIntentLoading && applyIntent?.eligibility?.primary_blocker ? (
              <div className="public-apply-confirm__gate">
                <strong>{applyIntent.eligibility.primary_blocker.title}</strong>
                <p>{applyIntent.eligibility.primary_blocker.detail}</p>
                {Array.isArray(applyIntent.eligibility.blockers) && applyIntent.eligibility.blockers.length > 1 ? (
                  <ul className="public-apply-confirm__blockers">
                    {applyIntent.eligibility.blockers.slice(1).map((entry) => (
                      <li key={entry.code}>{entry.title}</li>
                    ))}
                  </ul>
                ) : null}
                <CButton
                  type="button"
                  className="public-core-btn public-core-btn--light"
                  onClick={() => navigate(applyIntent.eligibility.primary_blocker.href)}
                >
                  {applyIntent.eligibility.primary_blocker.action_label}
                </CButton>
              </div>
            ) : null}

            {!applyIntentLoading && !applyIntent?.duplicate && applyIntent?.eligibility?.can_apply ? (
              <CAlert color="info" className="mb-0">
                Der Submit legt die Bewerbung echt an. Vorher wurden Duplikate und Applicant-Grundlagen geprüft.
              </CAlert>
            ) : null}
          </div>
        </CModalBody>
        <CModalFooter>
          <CButton
            type="button"
            className="public-core-btn public-core-btn--light"
            disabled={applyActionLoading}
            onClick={closeApplyDialog}
          >
            Abbruch
          </CButton>
          <CLoadingButton
            type="button"
            className="public-core-btn public-core-btn--subtle"
            disabledOnLoading
            loading={applyActionLoading}
            onClick={handleSaveJob}
          >
            Merken
          </CLoadingButton>
          <CLoadingButton
            type="button"
            className="public-core-btn public-core-btn--primary"
            disabledOnLoading
            disabled={applyIntentLoading || applyActionLoading || applyIntent?.duplicate || !applyIntent?.eligibility?.can_apply}
            loading={applyActionLoading}
            onClick={handleSubmitApplication}
          >
            Bewerben
          </CLoadingButton>
        </CModalFooter>
      </CModal>
    </PublicSiteShell>
  );
}
